/**
 * Query parameters that carry (or bound) an OAuth credential.
 *
 * The mobile app's B2C redirect lands on `/mobile-auth?code=…&state=…` whenever
 * the App Link is not honoured and the browser handles the URL instead. The SPA
 * reports page views to Application Insights, Sentry and GTM, so without
 * scrubbing, an authorization code would come to rest in three third-party
 * stores. PKCE bounds what the code is worth, but it is still a credential.
 */
const OAUTH_SENSITIVE_PARAMS = [
  'code',
  'state',
  'id_token',
  'access_token',
  'refresh_token',
  'session_state',
] as const;

/**
 * Returns `url` with any OAuth credential parameters replaced by `REDACTED`.
 *
 * Accepts absolute URLs and path-only values (as telemetry SDKs report either),
 * and returns the input unchanged when it cannot be parsed, so a malformed URL
 * can never throw inside a telemetry initializer.
 */
export const scrubOAuthParams = (url: string): string => {
  try {
    const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
    const parsed = new URL(url, isAbsolute ? undefined : window.location.origin);

    let didScrub = false;
    for (const param of OAUTH_SENSITIVE_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, 'REDACTED');
        didScrub = true;
      }
    }

    if (!didScrub) return url;
    return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

/** Path the mobile app's B2C redirect lands on when the App Link is not honoured. */
const MOBILE_AUTH_PATH = '/mobile-auth';

/**
 * Removes OAuth credential parameters from the address bar without adding a
 * history entry, so they are not re-sent by later navigations or referrers.
 *
 * Replaces the URL with the *scrubbed* one rather than the bare pathname, so any
 * unrelated query parameter or fragment the caller never asked to lose survives.
 */
export const stripOAuthParamsFromLocation = (): void => {
  if (typeof window === 'undefined' || !window.location.search) return;

  const scrubbed = scrubOAuthParams(window.location.href);
  if (scrubbed === window.location.href) return;

  window.history.replaceState(window.history.state, '', scrubbed);
};

/**
 * Scrubs the address bar before any telemetry SDK is initialised.
 *
 * Sentry's `breadcrumbsIntegration` wraps `history.replaceState` and reads
 * `window.location.href` *before* applying the original function, so a strip
 * performed later is itself recorded as a navigation breadcrumb carrying the
 * code. Breadcrumbs are attached during `prepareEvent`, ahead of `beforeSend`,
 * so the `request.url` hooks cannot reach them.
 *
 * Deliberately scoped to {@link MOBILE_AUTH_PATH}: the web portal's own MSAL
 * redirect (`/auth/chat`) needs its `code` and `state` intact, so an
 * unconditional strip here would break sign-in for the SPA itself.
 */
export const stripOAuthParamsBeforeTelemetryInit = (): void => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== MOBILE_AUTH_PATH) return;

  stripOAuthParamsFromLocation();
};
